import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tag, Space, Typography, Avatar, Empty, Pagination, theme } from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import { getFirstChar } from '../../../utils/string';
import { Gender } from '../../../constants/gender';
import { PatientCareLevel } from '../../../constants/careLevel';
import { IndependentLifeLevel } from '../../../constants/independentLifeLevel';
import { DentalIntervention } from '../../../constants/dentalIntervention';
import { formatYYYYMMDDSlash } from '../../../utils/date';
import useMobile from '../../../hooks/useMobile';

type PatientData = {
  key: string;
  id: string;
  name: string;
  age: number | undefined;
  gender: string;
  careLevel: string;
  lifeLevel: string;
  dentalIntervention: string;
  latestOhatScoreEvaluatedAt: string | undefined;
  /** 最新評価の合計点。評価が1件も無い場合は undefined */
  latestTotalScore: number | undefined;
  ohatScoreEvaluatedCount: number;
  /** 全OHAT評価に添付された口腔内写真の合計枚数 */
  photoCount: number;
  tags: { id: string; name: string }[];
  archivedAt?: string | null;
};

export type Props = {
  patients: PatientData[];
};

const PAGE_SIZE_OPTIONS = [5, 10, 20, 30, 50];
const DEFAULT_PAGE_SIZE = 20;

/**
 * 患者一覧。
 * antd の List は v6 で非推奨（次のメジャーで削除）となったため、
 * 従来の `List itemLayout="vertical" size="large"` 相当の見た目を
 * Pagination + 素の ul/li で再現している。
 */
export const PatientList: React.FC<Props> = ({ patients }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useMobile();
  const mobile = isMobile();
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // 絞り込みで件数が減ったときはページ番号を最終ページに丸める（旧 List と同じ挙動）
  const lastPage = Math.max(1, Math.ceil(patients.length / pageSize));
  const page = Math.min(current, lastPage);
  const pagedPatients = patients.slice((page - 1) * pageSize, page * pageSize);

  const onClick = (patientId: string) => {
    navigate(`/patients/${patientId}`);
  };

  return (
    <>
      {pagedPatients.length === 0 ? (
        <div style={{ padding: token.padding, textAlign: 'center' }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {pagedPatients.map((item) => (
            <li
              key={item.key}
              onClick={() => onClick(item.id)}
              className="hoverEffect"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                padding: `${token.paddingContentVerticalLG}px ${token.paddingContentHorizontalLG}px`,
                borderBlockEnd: `1px solid ${token.colorSplit}`,
                opacity: item.archivedAt ? 0.55 : undefined,
              }}
            >
              <Avatar
                style={{ flex: '0 0 auto', marginInlineEnd: token.padding }}
              >
                {getFirstChar(item.name)}
              </Avatar>
              <div style={{ flex: '1 0', width: 0 }}>
                <Typography.Text
                  style={{
                    fontSize: 18,
                    fontWeight: 'bold',
                    display: 'flex',
                    // モバイルは患者名の下の行に「写真枚数＋評価回数」を左寄せで積む
                    flexDirection: mobile ? 'column' : undefined,
                    alignItems: mobile ? 'flex-start' : undefined,
                    justifyContent: 'space-between',
                    marginBlockEnd: token.paddingSM,
                  }}
                >
                  {item.name}
                  <Typography.Text
                    style={{
                      fontSize: 12,
                      color: 'gray',
                      fontWeight: 'normal',
                    }}
                  >
                    {item.photoCount > 0 && (
                      <span style={{ whiteSpace: 'nowrap', marginRight: 8 }}>
                        <CameraOutlined style={{ marginRight: 4 }} />
                        {item.photoCount}
                      </span>
                    )}
                    {item.latestOhatScoreEvaluatedAt == null
                      ? t('patient.notAssessed')
                      : t('patient.assessSummary', {
                          count: item.ohatScoreEvaluatedCount,
                          date: formatYYYYMMDDSlash(
                            item.latestOhatScoreEvaluatedAt,
                          ),
                        })}
                  </Typography.Text>
                </Typography.Text>
                <Space size={[8, 8]} wrap>
                  {item.archivedAt && (
                    <Tag color="default">{t('patient.archivedBadge')}</Tag>
                  )}
                  <Tag
                    color={item.gender === Gender.female.key ? 'red' : 'blue'}
                  >{`${Gender.getByKey(item.gender).getLabel(
                    i18n.language,
                  )}`}</Tag>
                  {item.age && item.age !== 0 ? (
                    <Tag color="default">
                      {t('patient.ageValue', { age: item.age })}
                    </Tag>
                  ) : null}
                  {PatientCareLevel.getByKey(item.careLevel) !==
                    PatientCareLevel.none && (
                    <Tag color="volcano">{`${PatientCareLevel.getByKey(
                      item.careLevel,
                    ).getLabel(i18n.language)}`}</Tag>
                  )}
                  {IndependentLifeLevel.getByKey(item.lifeLevel) !==
                    IndependentLifeLevel.none && (
                    <Tag color="geekblue">{`${IndependentLifeLevel.getByKey(
                      item.lifeLevel,
                    ).getLabel(i18n.language)}`}</Tag>
                  )}
                  {DentalIntervention.getByKey(item.dentalIntervention) !==
                    DentalIntervention.none && (
                    <Tag color="blue">{`${DentalIntervention.getByKey(
                      item.dentalIntervention,
                    ).getLabel(i18n.language)}`}</Tag>
                  )}
                  {item.latestTotalScore != null && (
                    <Tag color="purple">
                      {t('patient.ohatTotalScoreValue', {
                        score: item.latestTotalScore,
                      })}
                    </Tag>
                  )}
                  {item.tags.map((tag) => (
                    <Tag key={tag.id} color="cyan">
                      {tag.name}
                    </Tag>
                  ))}
                </Space>
              </div>
            </li>
          ))}
        </ul>
      )}
      <Pagination
        align="end"
        style={{ marginBlockStart: token.marginLG }}
        current={page}
        pageSize={pageSize}
        total={patients.length}
        showSizeChanger
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onChange={(nextPage, nextPageSize) => {
          setCurrent(nextPage);
          setPageSize(nextPageSize);
        }}
        locale={{
          items_per_page: t('search.perPage'),
          jump_to: t('search.goToPage'),
        }}
      />
    </>
  );
};
